/**
 * Sidebar.tsx — боковое меню кабинета нового вида (WEB-3739 юнит 2), по `Side`
 * эталона `design/cabinet-rescin/sections/shell-x.jsx` (стили `.qm*` —
 * `styles/cabinet-shell.css`, итоговый вид «как лендинг»).
 *
 * Сверху вниз: логотип (знак и надпись по теме) → переключатель проекта
 * (`ProjectSwitcher`) → разделы группами с подписями («Проект» / «Управление»,
 * у организации — «Организация» / «Управление») → низ: «Поиск и ИИ ⌘K»
 * (открывает палитру) и строка профиля (`MeMenu`) с кнопкой «закрепить».
 *
 * Незакреплённое меню — узкая полоса 56; наведение и фокус с клавиатуры
 * раскрывают его поверх страницы (место держит пустой `.qm-ph`), уход —
 * свернуть через 180 мс, но не пока внутри открыто всплывающее
 * (`[aria-expanded=true]`). Закреплённое — раскрыто и сдвигает страницу;
 * «закреплено» запоминается (`useStored`, ключ `MENU_PINNED_KEY`). На телефоне
 * меню — шторкой сверху (`drawer`), закрывается свайпом влево больше 48 px.
 *
 * Режим «проект или организация» и активный пункт — по АДРЕСУ, логика прежнего
 * меню без изменений (WEB-438; нейтральные адреса — по выбранному контексту,
 * случай PM 10.08; прежний адрес A/B-тестов `/smart-links` — проектный, WEB-2013).
 * Пункты и правила показа — общий список `shellMenu.ts` (юнит 1): «Магазин»
 * скрыт у проекта со своими платежами, раздел контента у него — «Квизы»
 * (WEB-2126); гостю организации — только «Проекты» (WEB-761). Строку «Магазин
 * скрыт» владелец убрал 24.10. Корень `.ds` — у меню (решение оркестратора
 * 24.10: страница внутри `main` вне `.ds`).
 *
 * Доводка 1: под «Поиском и ИИ» — «Первые шаги» (`FirstStepsRow`, эталон
 * `shell-x.jsx:152`): только уровень проекта, ответ ручки `isNew === true`, не
 * узкое меню; ручку спрашиваем только на уровне проекта. На телефоне шторка выбора
 * проекта и меню профиля уходят в верхний слой браузера, их затемнение ложится и на
 * капсулу (как у эталона): это делает телефонный слой кита `PhoneRules`, но ставит
 * его не меню, а оболочка `router/DashboardLayout.tsx` (`everyRoot` — судит любой
 * корень `.ds`, и корень меню тоже; правило WEB-3310: слоями кита владеют только
 * витрина и оболочка, сторож `DashboardLayout.kitLayers.test.tsx`).
 *
 * WEB-3782: у пункта «Главная» (`nav-home`) — число строк блока «Требует
 * внимания» (`span.n`, подсказка «N требуют внимания»; эталон `shell-x.jsx`).
 * Список — у поставщика каркаса (`useProjectAttention`, тот же, что у блока
 * главной), счёт — та же `countAttention`: число не расходится со строками.
 * Ноль, первая загрузка, отказ ручки, режим организации — числа нет. В узком
 * меню — точка без цифры (правило `.qm.mini .qm-a .n`, `cabinet-shell.css`).
 * До WEB-3889 число показывается всегда, и у нового проекта тоже.
 */
import { Fragment, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Icon, IconButton, useStored } from '@/components/kit/ds';
import { useDraftEnabled } from '@/components/kit/useDraftEnabled';
import { MARK_SRC } from '@/components/brand/BrandLogo';
import { ProjectSwitcher } from '@/components/organizations/ProjectSwitcher';
import { useAppDispatch, useAppSelector } from '@/hooks/hooks';
// WEB-2126: денежный профиль проекта — один общий помощник на весь кабинет.
import { useOwnMoney } from '@/hooks/useProjectMoneyProfile';
import { useProjectFirstSteps } from '@/hooks/useProjectFirstSteps';
import { fetchOrganizationsThunk } from '@/store/organizationsSlice';
import { useStrings } from '@/i18n';
import { commonStrings } from '@/i18n/dictionaries/common';
import { attentionStrings } from '@/i18n/dictionaries/attention';
import { useProjectAttention } from '@/components/attention/ProjectAttentionProvider';
import { countAttention } from '@/components/attention/attentionCount';
import { useCabinetRootAttributes } from '@/theme/cabinetTokens';
import { MeMenu } from './MeMenu';
import { FirstStepsRow } from './FirstStepsRow';
import {
  orgShellMenu,
  projectShellMenu,
  shellFlat,
  shellLabel,
  type ShellMenuItem,
  type ShellMenuKey,
} from './shellMenu';
import '../../styles/cabinet-shell.css';

/** Фокус пришёл с клавиатуры (:focus-visible); движок без поддержки — считаем, что да. */
function focusVisible(el: EventTarget): boolean {
  try {
    return (el as Element).matches(':focus-visible');
  } catch {
    return true;
  }
}

/** Ключ «меню закреплено» в localStorage: '1' — да, '0' — нет (по умолчанию). */
export const MENU_PINNED_KEY = 'cabinet-menu-pinned';

const WORDMARK_SRC = {
  dark: '/brand/subster-wordmark-dark.svg',
  light: '/brand/subster-wordmark-light.svg',
};

const CONTENT_PATH = '/content';
// WEB-2013 (строка 7): канонический адрес «A/B-тестов» — '/ab-tests', прежний
// '/smart-links' — перенаправление; меню узнаёт раздел на обоих адресах.
const AB_TESTS_LEGACY_PATH = '/smart-links';

const ORG_ITEMS = shellFlat(orgShellMenu({ guest: false }));
/**
 * Все пункты проекта — для подсветки и режима меню. «Дашборд» проекта живёт на
 * `/home` (WEB-3748): адрес свой, ни с чего чужого не начинается — пункт входит
 * в список наравне с остальными.
 */
const PROJECT_ITEMS = shellFlat(projectShellMenu({ ownMoney: false, draft: true }));
/**
 * Адреса, по которым меню в режиме проекта, — прежний список меню (WEB-438):
 * ИИ-студия в него не входила, на её адресе режим по-прежнему решает выбранный
 * контекст.
 */
const PROJECT_MODE_ITEMS = PROJECT_ITEMS.filter((m) => m.key !== 'ai');

/**
 * Активный пункт меню проекта по адресу: `/content` (любая вкладка) — раздел
 * контента, `/ab-tests` и `/smart-links` — «A/B-тесты», `/settings` (личные
 * настройки) — ничего, остальное — пункт, с адреса которого начинается адрес;
 * незнакомый адрес — раздел контента (запасной, для прежних адресов).
 */
export function getProjectSelectedKey(pathname: string): ShellMenuKey | null {
  if (pathname.startsWith(CONTENT_PATH)) return 'content';
  if (pathname.startsWith('/ab-tests') || pathname.startsWith(AB_TESTS_LEGACY_PATH)) {
    return 'ab';
  }
  if (pathname.startsWith('/settings')) return null;
  const found = PROJECT_ITEMS.find(
    (m) => m.path !== CONTENT_PATH && pathname.startsWith(m.path),
  );
  return found?.key ?? 'content';
}

export interface ShellNav {
  level: 'org' | 'project';
  groups: ShellMenuItem[][];
  activeKey: ShellMenuKey | null;
}

/** Режим меню, группы пунктов и активный пункт — одна правда для меню и капсулы телефона. */
export function useShellNav(): ShellNav {
  const { pathname } = useLocation();
  const { orgs, activeOrgId } = useAppSelector((state) => state.organizations);
  // WEB-761 (AC-3): гостю организации орг-страницы — 403, в меню только «Проекты».
  const guest = orgs.find((o) => o.id === activeOrgId)?.role === 'member';
  const storedOrgMode = useAppSelector((state) => state.projects.orgMode);
  const currentProjectId = useAppSelector((state) => state.projects.currentProject?.id);
  const ownMoney = useOwnMoney(currentProjectId);
  const draft = useDraftEnabled();

  const isOrgPath = ORG_ITEMS.some((m) => pathname.startsWith(m.path));
  // Явное совпадение с проектными адресами: запасной пункт getProjectSelectedKey
  // для нейтрального адреса тут не годится (случай PM 10.08).
  const isProjectPath =
    pathname.startsWith(CONTENT_PATH) ||
    pathname.startsWith(AB_TESTS_LEGACY_PATH) ||
    PROJECT_MODE_ITEMS.some((m) => pathname.startsWith(m.path));
  if (isOrgPath || (!isProjectPath && storedOrgMode)) {
    return {
      level: 'org',
      groups: orgShellMenu({ guest }),
      activeKey: ORG_ITEMS.find((m) => pathname.startsWith(m.path))?.key ?? 'projects',
    };
  }
  return {
    level: 'project',
    groups: projectShellMenu({ ownMoney, draft }),
    activeKey: getProjectSelectedKey(pathname),
  };
}

export interface SidebarProps {
  /** Телефон: шторка меню открыта. */
  drawer?: boolean;
  /** Телефон: закрыть шторку (свайп влево). */
  onClose?: () => void;
  /** «Поиск и ИИ» — открыть палитру ⌘K. */
  onFind?: () => void;
}

export function Sidebar({ drawer = false, onClose, onFind }: SidebarProps) {
  const s = useStrings(commonStrings);
  const at = useStrings(attentionStrings);
  const attention = useProjectAttention();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const root = useCabinetRootAttributes(user?.theme);
  const { level, groups, activeKey } = useShellNav();
  const currentProjectId = useAppSelector((state) => state.projects.currentProject?.id);
  const firstSteps = useProjectFirstSteps(level === 'project' ? currentProjectId : undefined);
  const [pinValue, setPinValue] = useStored(MENU_PINNED_KEY, '0');
  const pinned = pinValue === '1';
  const [hov, setHov] = useState(false);
  const leave = useRef<ReturnType<typeof setTimeout>>();
  const side = useRef<HTMLElement>(null);
  const swipe = useRef<number | null>(null);
  const mini = !pinned && !hov && !drawer;

  // Организации — для меню профиля и прав переключателя (раньше грузила шапка).
  useEffect(() => {
    dispatch(fetchOrganizationsThunk());
  }, [dispatch]);
  useEffect(() => () => clearTimeout(leave.current), []);
  // Ушли из окна (другой экран, вкладка, программа) — mouseleave браузер не шлёт,
  // и раскрытое наведением меню осталось бы раскрытым: сворачиваем сами.
  useEffect(() => {
    const collapse = () => {
      clearTimeout(leave.current);
      setHov(false);
    };
    const hidden = () => {
      if (document.visibilityState === 'hidden') collapse();
    };
    window.addEventListener('blur', collapse);
    document.addEventListener('visibilitychange', hidden);
    return () => {
      window.removeEventListener('blur', collapse);
      document.removeEventListener('visibilitychange', hidden);
    };
  }, []);

  const enter = () => {
    clearTimeout(leave.current);
    setHov(true);
  };
  const out = () => {
    clearTimeout(leave.current);
    leave.current = setTimeout(() => {
      // Пока открыто всплывающее меню (проект, профиль) — ждём, пока его закроют.
      if (side.current?.querySelector('[aria-expanded=true]')) {
        out();
        return;
      }
      setHov(false);
    }, 180);
  };
  const togglePin = () => {
    setPinValue(pinned ? '0' : '1');
    if (pinned) setHov(false);
  };
  // Число у «Главной» — только в меню проекта (у организации пункта нет).
  const homeCount =
    level === 'project' ? countAttention(attention.data, attention.error) : 0;
  const secs =
    level === 'org'
      ? [s.organization, s.shellSecManage]
      : [s.shellSecProject, s.shellSecManage];

  return (
    <div {...root} className={`${root.className} shell-host`}>
      <div className={'qm-ph' + (pinned ? ' pin' : '')} aria-hidden="true" />
      <aside
        ref={side}
        className={'qm float' + (mini ? ' mini' : '') + (drawer ? ' open' : '')}
        onMouseEnter={enter}
        onMouseLeave={out}
        onFocus={(e) => {
          // Раскрываем по фокусу только с клавиатуры: при возврате в окно браузер
          // возвращает фокус на последний нажатый пункт — это не наведение.
          if (focusVisible(e.target)) enter();
        }}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) out();
        }}
        onTouchStart={(e) => {
          swipe.current = e.touches[0]?.clientX ?? null;
        }}
        onTouchEnd={(e) => {
          const from = swipe.current;
          swipe.current = null;
          const to = e.changedTouches[0]?.clientX;
          if (drawer && from != null && to != null && from - to > 48) onClose?.();
        }}
        data-testid="sidebar"
        aria-label={s.shellSideMenu}
      >
        <div className="qm-brand">
          <img src={MARK_SRC} alt="" width="36" height="36" />
          <img
            className="word"
            src={WORDMARK_SRC[root['data-theme']]}
            alt="Subster"
            height="27"
          />
        </div>
        <ProjectSwitcher />
        <nav className="qm-nav" aria-label={s.palSections} data-testid="menu">
          {groups.map((grp, gi) => {
            const sec = groups.length > 1 ? secs[gi] : undefined;
            return (
              <Fragment key={gi}>
                {gi > 0 && <div className="qm-gap" aria-hidden="true" />}
                {sec && (
                  <div className="qm-sec" data-testid={`qm-sec-${gi}`}>
                    {sec}
                  </div>
                )}
                {grp.map((item) => (
                  <Link
                    key={item.key}
                    to={item.path}
                    className="qm-a"
                    aria-current={activeKey === item.key ? 'page' : undefined}
                    // WEB-2298 п.1: почему раздел зовётся «Квизы» и кто может
                    // это изменить — подсказкой браузера, без новой строки меню.
                    title={
                      item.labelKey === 'navQuizzes' ? s.navQuizzesHint : undefined
                    }
                    data-testid={`nav-${item.key}`}
                  >
                    <Icon name={item.icon} size={16} />
                    <span className="sp">{shellLabel(item, s)}</span>
                    {item.key === 'home' && homeCount > 0 && (
                      <span
                        className="n"
                        title={at.navCount(homeCount)}
                        data-testid="nav-home-count"
                      >
                        {homeCount}
                      </span>
                    )}
                  </Link>
                ))}
              </Fragment>
            );
          })}
        </nav>
        <div className="qm-bot">
          <button type="button" className="qm-find" onClick={onFind} data-testid="find">
            <Icon name="search" size={16} />
            <span className="sp">{s.shellFind}</span>
            <span className="kbd">⌘K</span>
          </button>
          {level === 'project' && !mini && firstSteps?.isNew === true && (
            <FirstStepsRow steps={firstSteps.steps} />
          )}
          <div className="qm-me">
            <MeMenu placement="side" mini={mini} />
            {!mini && (
              <IconButton
                icon={pinned ? 'pinoff' : 'pin'}
                size="sm"
                label={pinned ? s.shellUnpin : s.shellPin}
                aria-pressed={pinned}
                onClick={togglePin}
                data-testid="pin"
              />
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}
